import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Tag } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface ReleaseNotes {
  [version: string]: string;
}

const components = {
  h1: (props: any) => (
    <h1
      className="text-base font-bold text-foreground mt-5 first:mt-0 mb-2"
      {...props}
    />
  ),
  h2: (props: any) => (
    <h2
      className="text-sm font-bold text-foreground mt-4 first:mt-0 mb-2"
      {...props}
    />
  ),
  h3: (props: any) => (
    <h3
      className="text-xs font-semibold text-foreground mt-4 first:mt-0 mb-2"
      {...props}
    />
  ),
  h4: (props: any) => (
    <h4
      className="text-xs font-semibold text-muted-foreground mt-3 mb-1"
      {...props}
    />
  ),
  p: (props: any) => (
    <p
      className="text-sm text-muted-foreground leading-relaxed my-2 break-words"
      {...props}
    />
  ),
  ul: (props: any) => (
    <ul
      className="space-y-1 my-2 pl-4 list-disc marker:text-muted-foreground/50"
      {...props}
    />
  ),
  ol: (props: any) => (
    <ol
      className="space-y-1 my-2 pl-4 list-decimal marker:text-muted-foreground/50"
      {...props}
    />
  ),
  li: (props: any) => (
    <li
      className="text-sm text-muted-foreground break-words [overflow-wrap:anywhere]"
      {...props}
    />
  ),
  strong: (props: any) => (
    <strong className="font-semibold text-foreground" {...props} />
  ),
  em: (props: any) => <em className="italic" {...props} />,
  a: (props: any) => (
    <a
      className="underline underline-offset-2 text-primary hover:text-primary/80 break-words [overflow-wrap:anywhere]"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  code: ({ className, children, ...props }: any) => {
    // react-markdown v9 no expone `inline`: detectamos bloque por el
    // className de lenguaje o por saltos de linea en el contenido.
    const isBlock =
      /language-/.test(className || "") || String(children).includes("\n");
    if (isBlock) {
      return (
        <code className={cn("font-mono text-xs", className)} {...props}>
          {children}
        </code>
      );
    }
    return (
      <code
        className="rounded bg-muted px-1 py-0.5 font-mono text-[0.8em] text-foreground [overflow-wrap:anywhere]"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: any) => (
    <pre
      className="my-2 overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs"
      {...props}
    >
      {children}
    </pre>
  ),
  blockquote: (props: any) => (
    <blockquote
      className="border-l-2 border-border pl-3 my-2 text-sm italic text-muted-foreground"
      {...props}
    />
  ),
  hr: (props: any) => <hr className="my-4 border-border" {...props} />,
  table: (props: any) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full border-collapse text-sm" {...props} />
    </div>
  ),
  thead: (props: any) => (
    <thead className="border-b border-border" {...props} />
  ),
  th: (props: any) => (
    <th className="px-2 py-1 text-left font-semibold text-foreground" {...props} />
  ),
  td: (props: any) => (
    <td
      className="border-b border-border/40 px-2 py-1 align-top text-muted-foreground"
      {...props}
    />
  ),
  img: (props: any) => (
    <img className="my-2 max-w-full rounded" alt="" {...props} />
  ),
};

function ReleaseNotesPage() {
  const [notes, setNotes] = useState<ReleaseNotes | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/release-notes.json")
      .then((res) => res.json())
      .then(setNotes)
      .catch(() => setNotes({}))
      .finally(() => setLoading(false));
  }, []);

  const versions = notes
    ? Object.entries(notes).sort(([a], [b]) => b.localeCompare(a))
    : [];

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" asChild className="h-8 w-8">
          <Link to="/dashboard">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-lg font-semibold">Historial de versiones</h1>
          <p className="text-xs text-muted-foreground">
            Actual: <span className="font-mono">{__GIT_VERSION__}</span>
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-5 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4 mb-2" />
                <Skeleton className="h-4 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : versions.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No hay notas de versión registradas.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {versions.map(([version, content]) => {
            const isCurrent = version === __GIT_VERSION__;
            return (
              <Card key={version}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Tag className="h-3.5 w-3.5 text-primary" />
                    <span className="font-mono">{version}</span>
                    {isCurrent && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-normal">
                        actual
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={components}
                  >
                    {content}
                  </ReactMarkdown>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/release-notes")({
  component: ReleaseNotesPage,
});
